import { useState, useEffect } from 'react';
import { normalizeUser } from '@/services/normalize-user.service';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { api } from '@/services/api';
import { createClient, supabase } from '@/services/supabase';
import { secureKeyService } from '@/services/secure-key.service';
import { toast } from 'react-toastify';
import { signOut } from '@/services/supabase';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import type { NormalizedUser } from '@/types/user-schema';

// Define the shape of the login form data
type LoginFormData = {
  email: string;
  password: string;
};

// Admin credentials from secure key service
// These will be base64 encoded in production
const ADMIN_EMAIL = secureKeyService.getKey('ADMIN_EMAIL');

export const SuperLoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [userData, setUserData] = useState<NormalizedUser | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
  } = useForm<LoginFormData>();

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        // Check if we have an existing session
        const supabase = await createClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          // Already logged in, redirect to appropriate dashboard
          // Use normalizeUser to create a consistent user object
          console.debug('Using normalizeUser in SuperLogin checkExistingSession');
          const normalizedUser = normalizeUser(session.user);
          
          // Set user in state
          setUserData(normalizedUser as NormalizedUser);
          
          // Redirect to dashboard if already logged in
          navigate('/super-admin/dashboard');
          // Keep original implementation for backward compatibility

          if (normalizedUser.role === 'super_admin' || session.user.email === ADMIN_EMAIL) {
            console.log('Found existing super admin session');
            setLoginSuccess(true);
            setUserData(normalizedUser as NormalizedUser);
            navigate('/super-admin/dashboard');
          } else {
            // Not a super admin, sign out
            await supabase.auth.signOut();
            console.log('Not a super admin, signed out');
          };
        }
      } catch (err) {
        console.error('Error checking session:', err);
      }
    };

    // Set up auth state change listener
    const setupAuthListener = async () => {
      const supabase = await createClient();
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event: AuthChangeEvent, session: Session | null) => {
          console.log('Auth state changed:', event, session?.user?.email);

          if (event === 'SIGNED_IN' && session?.user) {
            const user = session.user;
            const userRole = user.user_metadata?.role || user.role;

            if (userRole === 'super_admin' || user.email === ADMIN_EMAIL) {
              setLoginSuccess(true);
              setUserData(normalizeUser(user) as NormalizedUser);
            }
          } else if (event === 'SIGNED_OUT') {
            setLoginSuccess(false);
            setUserData(null);
          }
        }
      );

      return subscription;
    };

    // Execute the async functions
    checkExistingSession();
    const subscriptionPromise = setupAuthListener();

    // Cleanup function
    return () => {
      subscriptionPromise.then(subscription => {
        subscription.unsubscribe();
      }).catch(err => {
        console.error('Error unsubscribing from auth listener:', err);
      });
    };
  }, [navigate]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use Supabase directly for authentication
      const supabase = await createClient();
      
      // Sign in with email and password
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });
      
      if (error) {
        console.error('Supabase login error:', error);
        setError(error.message || 'Invalid credentials');
        setIsLoading(false);
        return;
      }
      
      if (authData?.user) {
        // Process the user through our normalization function
        const normalizedUser = normalizeUser(authData.user);
        console.log('Normalized super admin user:', normalizedUser);
        
        // Store the user data in state
        setUserData(normalizedUser as NormalizedUser);
        
        // Check if the user has super_admin role
        const userRole = normalizedUser.role;
        
        if (userRole !== 'super_admin' && normalizedUser.email !== ADMIN_EMAIL) {
          // Sign out if not a super admin
          await supabase.auth.signOut();
          setError('You do not have super admin privileges');
          setIsLoading(false);
          return;
        }
        
        // Update user metadata to ensure super_admin role
        await supabase.auth.updateUser({
          data: {
            role: 'super_admin'
          }
        });
        
        // Update state
        setLoginSuccess(true);
        
        // Set API headers for any API calls
        if (authData.session) {
          api.defaults.headers.common['Authorization'] = `Bearer ${authData.session.access_token}`;
        }
        
        toast.success('Login successful');
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard');
        return;
      }
      
      setError('Authentication failed. Please check your credentials.');
      setIsLoading(false);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred');
      setIsLoading(false);
    }
  };

  // Dev mode login is now handled in the onSubmit function

  // Render the dashboard selection after successful login
  // Check the role directly from user metadata
  if (loginSuccess && (userData?.role === 'super_admin' || userData?.user_metadata?.role === 'super_admin')) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center bg-white">
        <div className="w-full px-8 py-12">
          <div className="mx-auto max-w-md">
            <div className="text-center">
              <img 
                src="/favicon.png" 
                alt="Catch of the Day Logo" 
                className="h-16 w-auto mx-auto mb-4"
              />
              <h2 className="mt-6 text-center text-3xl font-glacial font-bold text-navy-blue">
                Super Admin Dashboard
              </h2>
              <p className="mt-2 text-center text-sm text-dark-gray">
                Welcome back, {userData.email}
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-medium text-navy-blue">Select a portal to access:</h3>

              <div className="grid grid-cols-1 gap-4">
                <button
                  onClick={async () => {
                    // Get current session directly from Supabase
                    const supabase = await createClient();
                    const { data: { session } } = await supabase.auth.getSession();
                    console.log('Current Supabase session:', session);

                    if (session?.user) {
                      // Process the user through our normalization function
                      const normalizedUser = normalizeUser(session.user);
                      console.log('Normalized user:', normalizedUser);

                      // Create a super admin version of the normalized user
                      // Use type assertion for the final object to ensure it matches NormalizedUser
                      const superAdminUser = {
                        ...normalizedUser,
                        role: 'super_admin' as const,
                        super_admin_access: true,
                        business_access: true,
                        charity_access: true,
                        user_metadata: {
                          ...((normalizedUser as unknown as { user_metadata: Record<string, unknown> }).user_metadata || {}),
                          role: 'super_admin'
                        },
                        app_metadata: {
                          ...((normalizedUser as unknown as { app_metadata: Record<string, unknown> }).app_metadata || {}),
                          super_admin: true,
                          super_admin_access: true,
                          business_access: true,
                          charity_access: true
                        }
                      } as NormalizedUser;

                      // Create a business version of the session that preserves super_admin access
                      const businessSession = {
                        ...session,
                        user: superAdminUser
                      };

                      console.log('Business session to store:', businessSession);

                      // Update the session for business portal access
                      // Store business session in localStorage
                      localStorage.setItem('supabase_session', JSON.stringify(businessSession));

                      // Set up API headers
                      api.defaults.headers.common['Authorization'] = `Bearer ${businessSession.access_token}`;

                      // Set a longer session expiration time for super admins (3 hours)
                      // We're now using Supabase as the single source of truth for sessions
                      // No need to manually store the session as Supabase handles this internally
                      // The session data is already stored by Supabase auth.getSession()

                      // IMPORTANT: Navigate to the correct business route
                      // Use the route path that matches exactly what's in App.tsx
                      console.log('Navigating to business dashboard...');
                      navigate('/dashboard'); // Changed from /businesses to /dashboard
                    } else {
                      toast.error('Session expired. Please login again.');
                    }
                  }}
                  className="flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal transition-colors duration-200"
                >
                  Business Dashboard
                </button>

                <button
                  onClick={async () => {
                    // DEBUGGING: Add extensive console logging
                    console.log('Attempting to navigate to Charity Dashboard');
                    // Get current session directly from Supabase
                    const { data: { session } } = await supabase.auth.getSession();
                    console.log('Current Supabase session:', session);

                    if (session?.user) {
                      // Process the user through our normalization function
                      const normalizedUser = normalizeUser(session.user);
                      console.log('Normalized user:', normalizedUser);

                      // Create a charity version of the session that preserves super_admin access
                      // but adds the charity role and access flags
                      const charitySession = {
                        ...session,
                        user: {
                          ...normalizedUser,
                          // Important: Keep original role as super_admin to maintain privileges
                          // but add charity role tag for route access
                          role: 'super_admin' as const,
                          // Ensure metadata has both super_admin and charity role references
                          user_metadata: {
                            ...((normalizedUser as unknown as { user_metadata: Record<string, unknown> }).user_metadata || {}),
                            role: 'super_admin',
                            charity_role: 'charity'
                          },
                          // Add app_metadata to ensure access is granted
                          app_metadata: {
                            ...((normalizedUser as unknown as { app_metadata: Record<string, unknown> }).app_metadata || {}),
                            super_admin: true,
                            charity_access: true
                          }
                        } as NormalizedUser
                      };

                      console.log('Charity session to store:', charitySession);

                      // Update the session for charity portal access
                      // Store charity session in localStorage
                      localStorage.setItem('supabase_session', JSON.stringify(charitySession));

                      // Set up API headers
                      api.defaults.headers.common['Authorization'] = `Bearer ${charitySession.access_token}`;

                      // Set a longer session expiration time for super admins (3 hours)
                      // Update the session with a longer expiration time


                      // IMPORTANT: Navigate to the correct charity route
                      // Use the route path that matches exactly what's in App.tsx
                      console.log('Navigating to charity dashboard...');
                      navigate('/charity'); // Changed from /charities to /charity to match App.tsx routes
                    } else {
                      toast.error('Session expired. Please login again.');
                    }
                  }}
                  className="flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors duration-200"
                >
                  Charity Dashboard
                </button>

                <button
                  onClick={() => navigate('/super-register')}
                  className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  Register New Users
                </button>
              </div>

              <div className="mt-6">
                <button
                  onClick={async () => {
                    // Use the centralized signOut method from supabase.ts
                    await signOut();

                    // Clear API headers
                    delete api.defaults.headers.common['Authorization'];
                    setLoginSuccess(false);
                    setUserData(null);
                    toast.info('Logged out successfully');
                  }}
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main login form
  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-white">
      <div className="w-full px-8 py-12">
        <div className="mx-auto max-w-md">
          <div className="text-center">
            <img 
              src="/favicon.png" 
              alt="Catch of the Day Logo" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="mt-6 text-center text-3xl font-glacial font-bold text-navy-blue">
              Super Admin Access
            </h2>
            <p className="mt-2 text-center text-sm text-dark-gray">
              Access all system features without restrictions
            </p>
          </div>

          <div className="max-w-md w-full space-y-8 p-6 bg-white shadow-md rounded-xl mt-6">
            <div className="text-center">
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Super Admin Login</h2>
              <p className="mt-2 text-sm text-gray-600">
                <Link to="/" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                  Return to homepage
                </Link>
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
              <div className="rounded-md shadow-sm">
                <div>
                  <label htmlFor="email-address" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email-address"
                    type="email"
                    autoComplete="email"
                    required
                    className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    placeholder="Email address"
                    {...register('email')}
                  />
                </div>
                <div className="relative">
                  <label htmlFor="password" className="sr-only">
                    Password
                  </label>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="relative block w-full appearance-none rounded-none rounded-b-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    placeholder="Password"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2 text-gray-500"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-100 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Loading...' : 'Sign in'}
                </button>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm">
                  <Link to="/login" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                    Regular Login
                  </Link>
                </div>
                <div className="text-sm">
                  <Link to="/super-register" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                    Create New User
                  </Link>
                </div>
              </div>
            </form>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Super Admin Help</h3>
              <p className="text-xs text-gray-500">
                For super admin access, use the credentials provided to you.
                As Super Admin, you'll have access to all system features without restrictions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
